/**
 * Keyboard triage for Now and Inbox (docs/ux/project-tab-spec.md §1.9): j/k, Enter, Esc, 1–9,
 * s, r, d, e, x, ⇧x, g p, ?. Single keys are inactive while typing. Listens in the capture phase
 * so `g p` (row's project) wins over the shell's `g p` (Projects) while a row has focus.
 */
import { useEffect, useRef, useState } from 'react'
import { type TriageAction, triageKey } from '../../lib/now-inbox.js'
import { isTyping } from '../../lib/shortcut.js'

export function useTriageKeys(o: {
  keys: readonly string[]
  enabled?: boolean
  /** Handled keys stop here (a drawer over the Lanes tab owns j/k while open). */
  exclusive?: boolean
  onAction: (a: TriageAction, state: { focus: number }) => void
  /** The inspected row: j/k step from it, and `onMove` swaps the inspector's content. */
  current?: string
  onMove?: (key: string) => void
  /** Where rows live (for moving DOM focus with the cursor). */
  root: React.RefObject<HTMLElement | null>
}) {
  const [focus, setFocus] = useState(-1)
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const [anchor, setAnchor] = useState<number>()
  const g = useRef(0)
  const latest = useRef({ ...o, focus, selected, anchor })
  latest.current = { ...o, focus, selected, anchor }

  // Keep the cursor on a row that still exists.
  useEffect(() => {
    if (focus >= o.keys.length) setFocus(o.keys.length - 1)
    setSelected((cur) => {
      const next = new Set([...cur].filter((k) => o.keys.includes(k)))
      return next.size === cur.size ? cur : next
    })
  }, [o.keys, focus])

  const moveTo = (i: number) => {
    setFocus(i)
    const key = latest.current.keys[i]
    if (!key) return
    requestAnimationFrame(() => {
      const el = latest.current.root.current?.querySelector<HTMLElement>(
        `[data-row-key="${CSS.escape(key)}"] [data-row-main]`,
      )
      el?.focus()
      el?.scrollIntoView({ block: 'nearest' })
    })
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: reads the latest state through refs
  useEffect(() => {
    if (o.enabled === false) return
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (isTyping(e) || document.querySelector('[role="dialog"][aria-modal="true"]')) return
      const s = latest.current
      let key = e.key
      // Enter belongs to whatever control has focus unless that is a row (or nothing).
      const t = e.target as HTMLElement | null
      const inList = !t || t === document.body || !!s.root.current?.contains(t)
      if (key === 'Enter' && !(inList && (t?.hasAttribute('data-row-main') || t === document.body)))
        return
      // ↑/↓ inside an open decision move its option cursor (design-language §7b); j/k still step.
      if ((key === 'ArrowDown' || key === 'ArrowUp') && t?.closest('[data-owns-arrows]')) return
      if (key === 'g') {
        g.current = Date.now()
        return
      }
      if (key === 'p' && Date.now() - g.current < 1200 && s.focus >= 0) {
        key = 'gp'
        e.stopImmediatePropagation()
      }
      g.current = 0
      const at = s.focus < 0 && s.current ? s.keys.indexOf(s.current) : -1
      const a = triageKey(
        { key, shift: e.shiftKey },
        {
          keys: s.keys,
          focus: at >= 0 ? at : s.focus,
          selected: s.selected,
          ...(s.anchor !== undefined ? { anchor: s.anchor } : {}),
        },
      )
      if (!a) return
      // Keys that act on "the focused row" need one; the first j/k picks the top row.
      if (a.type === 'move') {
        e.preventDefault()
        if (s.exclusive) e.stopImmediatePropagation()
        const to = s.focus < 0 && at < 0 ? 0 : a.index
        moveTo(to)
        const k = s.keys[to]
        if (s.current && k && k !== s.current) s.onMove?.(k)
        return
      }
      if (a.type !== 'help' && a.type !== 'close' && s.focus < 0) return
      if (a.type === 'select') {
        setSelected(new Set(a.keys))
        setAnchor(a.anchor)
      }
      e.preventDefault()
      if (s.exclusive) e.stopImmediatePropagation()
      s.onAction(a, { focus: s.focus })
    }
    addEventListener('keydown', onKey, true)
    return () => removeEventListener('keydown', onKey, true)
  }, [o.enabled])

  return {
    focus,
    setFocus: (i: number) => setFocus(i),
    moveTo,
    selected,
    setSelected: (keys: Iterable<string>) => setSelected(new Set(keys)),
    toggle: (key: string) => {
      const i = latest.current.keys.indexOf(key)
      setSelected((cur) => {
        const next = new Set(cur)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        return next
      })
      if (i >= 0) setAnchor(i)
    },
  }
}
